import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { getTotalUsers } from "../../../api/getOverviewData";
import { DateCount } from "../../../types/dateValue";
import SmallOverviewWithIcon from "./SmallOverviewWithIcon";
import { Skeleton } from "@mui/material";
import { getTheme } from "../../../theme";
import GroupIcon from "@mui/icons-material/Group";
import { set } from "date-fns";

type TotalUsersProps = {
  projectCodes: string[];
  height: number;
};

export type TotalUsersRef = {
  isReady: () => boolean;
  getData: () => number[];
};

const TotalUsers = forwardRef<TotalUsersRef, TotalUsersProps>(
  function TotalUsers({ projectCodes, height }, ref) {
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<number[]>([]);
    const theme = getTheme();

    useImperativeHandle(ref, () => ({
      isReady: () => isLoading,
      getData: () => data,
    }));

    useEffect(() => {
      setIsLoading(true);
      if (projectCodes.length === 0) return;
      let controller = new AbortController();
      let isMounted = true;
      const fetchTotalUsers = async () => {
        try {
          const data = await getTotalUsers(projectCodes, controller);
          if (isMounted) {
            setData(data);
          }
        } catch (error: any) {
          if (error.name === "AbortError") {
            console.log("Fetch aborted");
          } else {
            console.error("Fetch error:", error);
          }
        } finally {
          if (isMounted) {
            setIsLoading(false);
          }
        }
      };

      fetchTotalUsers();

      return () => {
        isMounted = false;
        controller.abort(); // Cancel the previous request if a new one starts
      };
    }, [projectCodes]);

    return data.length > 0 && isLoading === false ? (
      <SmallOverviewWithIcon
        height={height}
        titleMain="Total Users"
        titleSecondary="today"
        contentMain={data[0]}
        contentSecondary={0}
        icon={GroupIcon}
        iconColor={theme.palette.primaryChart}
      />
    ) : (
      <Skeleton
        sx={{
          borderRadius: "5px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.2)",
        }}
        animation="pulse"
        variant="rectangular"
        width={"100%"}
        height={height}
      />
    );
  }
);

export {TotalUsers}